(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let lenis;
  let lenisTicker;
  let motionContext;
  let resizeTimer;

  function initHeaderSubmenus(scope = document) {
    scope.querySelectorAll('.header__inline-menu header-menu > details').forEach((menu) => {
      if (menu.dataset.dingAccordionReady === 'true') return;
      menu.dataset.dingAccordionReady = 'true';

      const submenus = Array.from(
        menu.querySelectorAll(':scope > .header__submenu > li > details')
      );

      const closeSubmenus = (except) => {
        submenus.forEach((submenu) => {
          if (submenu !== except && submenu.open) submenu.open = false;
        });
      };

      submenus.forEach((submenu) => {
        submenu.addEventListener('toggle', () => {
          if (submenu.open) closeSubmenus(submenu);
        });
      });

      menu.addEventListener('toggle', () => {
        if (!menu.open) closeSubmenus();
      });

      document.addEventListener('pointerdown', (event) => {
        if (menu.open && !menu.contains(event.target)) menu.open = false;
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !menu.open) return;
        closeSubmenus();
        menu.open = false;
        menu.querySelector(':scope > summary')?.focus();
      });
    });
  }

  function startLenis() {
    if (reduceMotion.matches || typeof window.Lenis !== 'function' || lenis) return;

    lenis = new window.Lenis({
      duration: 1.05,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      syncTouch: false,
      wheelMultiplier: 0.82,
      touchMultiplier: 1,
      anchors: true,
    });

    lenis.on('scroll', window.ScrollTrigger.update);
    lenisTicker = (time) => lenis?.raf(time * 1000);
    window.gsap.ticker.add(lenisTicker);
    window.gsap.ticker.lagSmoothing(0);
  }

  function stopLenis() {
    if (!lenis) return;
    if (lenisTicker && window.gsap) window.gsap.ticker.remove(lenisTicker);
    lenis.destroy();
    lenis = undefined;
    lenisTicker = undefined;
  }

  function initMotion() {
    if (!window.gsap || !window.ScrollTrigger) return;

    const { gsap, ScrollTrigger } = window;
    gsap.registerPlugin(ScrollTrigger);
    document.documentElement.classList.add('ding-motion-ready');

    if (motionContext) motionContext.revert();

    motionContext = gsap.context(() => {
      const media = gsap.matchMedia();

      media.add('(prefers-reduced-motion: no-preference)', () => {
        startLenis();

        const heroStage = document.querySelector('[data-ding-hero-stage]');
        if (heroStage) {
          const heroVisual = heroStage.querySelector('[data-ding-hero-visual]');
          const heroImage = heroVisual?.querySelector('img');
          const heroRail = heroStage.querySelector('[data-ding-hero-rail]');
          const heroTimeline = gsap.timeline({
            defaults: { ease: 'expo.out' },
          });

          heroTimeline
            .to(
              '[data-ding-hero-curtain]',
              {
                xPercent: (index) => (index ? 102 : -102),
                duration: 1.05,
                stagger: 0.05,
                ease: 'power4.inOut',
              },
              0.08
            )
            .from(
              '[data-ding-hero-word]',
              { autoAlpha: 0, scale: 0.72, duration: 1.35 },
              0.14
            )
            .from(
              '[data-ding-hero-spotlight], [data-ding-hero-orbit]',
              { autoAlpha: 0, scale: 0.65, rotation: -24, duration: 1.45, stagger: 0.08 },
              0.16
            )
            .fromTo(
              heroVisual,
              { autoAlpha: 0, y: 110, scale: 0.68, rotationX: 14, rotationY: -10 },
              {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                rotationX: 0,
                rotationY: 0,
                duration: 1.35,
                ease: 'expo.out',
              },
              0.22
            )
            .from(
              '[data-ding-hero-copy]',
              { autoAlpha: 0, y: 46, duration: 0.86, stagger: 0.085, ease: 'power4.out' },
              0.54
            )
            .from(
              '[data-ding-hero-glass]',
              { autoAlpha: 0, x: 34, y: 24, duration: 0.7, ease: 'power3.out' },
              0.82
            )
            .from(
              '[data-ding-hero-bean]',
              {
                autoAlpha: 0,
                x: (index) => (index % 2 ? -150 : 170),
                y: (index) => (index % 3 ? 120 : -130),
                scale: 0,
                rotation: (index) => (index % 2 ? -160 : 180),
                duration: 1.05,
                stagger: { amount: 0.42, from: 'random' },
                ease: 'back.out(1.45)',
              },
              0.38
            );

          if (heroImage) {
            heroTimeline.from(heroImage, { scale: 1.28, duration: 1.7, ease: 'power3.out' }, 0.2);
          }

          if (heroRail) {
            gsap.to(heroRail, {
              xPercent: -35,
              duration: 22,
              repeat: -1,
              ease: 'none',
            });
          }

          gsap.to(heroVisual, {
            yPercent: 11,
            rotationX: 3,
            ease: 'none',
            scrollTrigger: {
              trigger: heroStage,
              start: 'top top',
              end: 'bottom top',
              scrub: 0.9,
            },
          });

          gsap.to('[data-ding-hero-word]', {
            yPercent: -18,
            scale: 1.07,
            ease: 'none',
            scrollTrigger: {
              trigger: heroStage,
              start: 'top top',
              end: 'bottom top',
              scrub: 1.1,
            },
          });
        }

        const innerHero = document.querySelector('[data-ding-inner-hero]');
        if (innerHero) {
          const innerHeroCopy = innerHero.querySelectorAll(
            '.collection-hero__text-wrapper > *, .main-page-title, .title--primary, .template-search__header > *'
          );
          const innerHeroVisual = innerHero.querySelector(
            '.collection-hero__image-container, .article-template__hero-container'
          );

          if (innerHeroCopy.length) {
            gsap.from(innerHeroCopy, {
              autoAlpha: 0,
              y: 48,
              duration: 0.92,
              stagger: 0.09,
              ease: 'power4.out',
              clearProps: 'opacity,visibility,transform',
            });
          }

          if (innerHeroVisual) {
            gsap.from(innerHeroVisual, {
              autoAlpha: 0,
              y: 56,
              scale: 0.92,
              rotationY: -7,
              duration: 1.15,
              ease: 'expo.out',
              clearProps: 'opacity,visibility,transform',
            });
          }
        }

        const productStage = document.querySelector('.ding-product-stage');
        if (productStage) {
          const productEntrance = gsap.timeline({ defaults: { ease: 'power4.out' } });
          productEntrance
            .from('.product__media-wrapper', { autoAlpha: 0, x: -54, rotationY: 5, duration: 1.05 })
            .from(
              '.product__info-container > *',
              { autoAlpha: 0, y: 28, duration: 0.68, stagger: 0.055 },
              0.28
            );
        }

        gsap.utils.toArray('[data-ding-inner-orbit]').forEach((orbit) => {
          const container = orbit.closest(
            '[data-ding-inner-hero], .ding-product-stage, .ding-article-stage'
          );
          gsap.to(orbit, {
            rotation: 24,
            xPercent: -8,
            ease: 'none',
            scrollTrigger: {
              trigger: container || orbit,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 1.2,
            },
          });
        });

        gsap.utils.toArray('[data-ding-inner-bean]').forEach((bean, index) => {
          const container = bean.closest(
            '[data-ding-inner-hero], .ding-product-stage, .ding-article-stage'
          );
          gsap.to(bean, {
            xPercent: index % 2 ? -65 : 75,
            yPercent: index % 3 ? -80 : 90,
            rotation: index % 2 ? -100 : 125,
            ease: 'none',
            scrollTrigger: {
              trigger: container || bean,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 1.35,
            },
          });
        });

        const articleStage = document.querySelector('.ding-article-stage');
        if (articleStage) {
          const articleCover = articleStage.querySelector('.article-template__hero-container');
          const articleHeader = articleStage.querySelector(':scope > header');
          if (articleCover) {
            gsap.from(articleCover, {
              autoAlpha: 0,
              y: 46,
              scale: 0.95,
              duration: 1.05,
              ease: 'expo.out',
            });
          }
          if (articleHeader) {
            gsap.from(articleHeader, {
              autoAlpha: 0,
              y: 58,
              duration: 0.9,
              delay: 0.25,
              ease: 'power4.out',
            });
          }
          gsap.utils.toArray('.article-template__content > *').forEach((element) => {
            gsap.from(element, {
              autoAlpha: 0,
              y: 30,
              duration: 0.72,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: element,
                start: 'top 90%',
                once: true,
              },
            });
          });
        }

        gsap.from('[data-ding-header] .header > *', {
          autoAlpha: 0,
          y: -16,
          duration: 0.8,
          stagger: 0.07,
          ease: 'power3.out',
          clearProps: 'opacity,visibility,transform',
        });

        const hero = document.querySelector('[id*="image_banner_34zDeN"]');
        if (hero) {
          const heroMedia = hero.querySelector('.banner__media');
          const heroTimeline = gsap.timeline({ defaults: { ease: 'power3.out' } });
          heroTimeline.from(hero, { autoAlpha: 0, y: 24, duration: 0.9 });

          if (heroMedia) {
            heroTimeline.from(heroMedia, { scale: 1.09, duration: 1.35 }, 0);
            gsap.to(heroMedia, {
              yPercent: 6,
              ease: 'none',
              scrollTrigger: {
                trigger: hero,
                start: 'top top',
                end: 'bottom top',
                scrub: 0.8,
              },
            });
          }
        }

        gsap.to('[data-ding-rings]', {
          rotation: 18,
          yPercent: 10,
          ease: 'none',
          scrollTrigger: {
            trigger: document.documentElement,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 1.2,
          },
        });

        gsap.to('[data-ding-spotlight]', {
          xPercent: 22,
          yPercent: 14,
          ease: 'none',
          scrollTrigger: {
            trigger: document.documentElement,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 1.5,
          },
        });

        gsap.utils.toArray('[data-ding-orb]').forEach((orb, index) => {
          gsap.to(orb, {
            xPercent: index ? -18 : 22,
            yPercent: index ? -24 : 18,
            rotation: index ? -16 : 14,
            ease: 'none',
            scrollTrigger: {
              trigger: document.documentElement,
              start: 'top top',
              end: 'bottom bottom',
              scrub: 1.4 + index * 0.25,
            },
          });
        });

        gsap.to('[data-ding-floating-bean]', {
          yPercent: (index) => (index % 2 ? -90 : 110),
          xPercent: (index) => (index % 2 ? 45 : -35),
          rotation: (index) => (index % 2 ? 85 : -70),
          ease: 'none',
          stagger: 0.08,
          scrollTrigger: {
            trigger: document.documentElement,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 1.6,
          },
        });

        const revealItems = [
          '#MainContent .title-wrapper-with-link',
          '#MainContent .collection__title',
          '#MainContent .collage-wrapper-title',
          '#MainContent .multicolumn .title-wrapper-with-link',
          '#MainContent .image-with-text__content > *',
        ];

        ScrollTrigger.batch(revealItems.join(','), {
          start: 'top 88%',
          once: true,
          onEnter: (elements) =>
            gsap.fromTo(
              elements,
              { autoAlpha: 0, y: 30 },
              { autoAlpha: 1, y: 0, duration: 0.72, stagger: 0.08, ease: 'power3.out', overwrite: true }
            ),
        });

        ScrollTrigger.batch(
          '#MainContent .card-wrapper, #MainContent .multicolumn-card, #MainContent .collage-card',
          {
            start: 'top 92%',
            once: true,
            onEnter: (elements) =>
              gsap.fromTo(
                elements,
                { autoAlpha: 0, y: 42, scale: 0.97 },
                {
                  autoAlpha: 1,
                  y: 0,
                  scale: 1,
                  duration: 0.68,
                  stagger: 0.055,
                  ease: 'power3.out',
                  overwrite: true,
                  clearProps: 'transform',
                }
              ),
          }
        );

        gsap.utils.toArray('#MainContent .collage-card .media, #MainContent .image-with-text__media').forEach((mediaItem) => {
          gsap.fromTo(
            mediaItem,
            { yPercent: -3 },
            {
              yPercent: 3,
              ease: 'none',
              scrollTrigger: {
                trigger: mediaItem,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.8,
              },
            }
          );
        });

        ScrollTrigger.batch(
          '.template-page #MainContent .multirow__inner > div, .template-page #MainContent .collapsible-content__wrapper, .template-cart .cart__contents, .template-cart .cart__footer',
          {
            start: 'top 90%',
            once: true,
            onEnter: (elements) =>
              gsap.fromTo(
                elements,
                { autoAlpha: 0, y: 38, scale: 0.98 },
                {
                  autoAlpha: 1,
                  y: 0,
                  scale: 1,
                  duration: 0.76,
                  stagger: 0.08,
                  ease: 'power3.out',
                  clearProps: 'transform',
                }
              ),
          }
        );

        gsap.from('[data-ding-footer] .ding-footer__brand-mark, [data-ding-footer] .footer-block, [data-ding-footer] .footer-block--newsletter', {
          autoAlpha: 0,
          y: 30,
          duration: 0.75,
          stagger: 0.075,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '[data-ding-footer]',
            start: 'top 84%',
            once: true,
          },
        });

        gsap.to('[data-ding-footer] .ding-footer__scene svg', {
          yPercent: -10,
          scale: 1.06,
          ease: 'none',
          scrollTrigger: {
            trigger: '[data-ding-footer]',
            start: 'top bottom',
            end: 'bottom bottom',
            scrub: 1,
          },
        });
      });

      media.add('(min-width: 990px) and (prefers-reduced-motion: no-preference)', () => {
        const cleanups = [];
        const heroVisual = document.querySelector('[data-ding-hero-visual]');
        if (heroVisual) {
          const hero = document.querySelector('[data-ding-hero-stage]');
          const rotateX = gsap.quickTo(heroVisual, 'rotationX', { duration: 0.5, ease: 'power3.out' });
          const rotateY = gsap.quickTo(heroVisual, 'rotationY', { duration: 0.5, ease: 'power3.out' });
          const onHeroMove = (event) => {
            const bounds = heroVisual.getBoundingClientRect();
            rotateX(((event.clientY - bounds.top) / bounds.height - 0.5) * -4);
            rotateY(((event.clientX - bounds.left) / bounds.width - 0.5) * 5);
          };
          const onHeroLeave = () => {
            rotateX(0);
            rotateY(0);
          };
          hero?.addEventListener('pointermove', onHeroMove, { passive: true });
          hero?.addEventListener('pointerleave', onHeroLeave, { passive: true });
          cleanups.push(() => {
            hero?.removeEventListener('pointermove', onHeroMove);
            hero?.removeEventListener('pointerleave', onHeroLeave);
          });
        }

        const journey = document.querySelector('[data-ding-journey]');
        if (!journey) return () => cleanups.forEach((cleanup) => cleanup());

        const shell = journey.querySelector('[data-ding-journey-shell]');
        const track = journey.querySelector('[data-ding-journey-track]');
        const panels = gsap.utils.toArray('[data-ding-journey-panel]', journey);
        const progress = journey.querySelector('[data-ding-journey-progress]');
        const distance = () => Math.max(0, track.scrollWidth - window.innerWidth);

        gsap.from('.ding-journey__intro > *, .ding-journey__intro-grid > *', {
          autoAlpha: 0,
          y: 44,
          duration: 0.9,
          stagger: 0.09,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: journey,
            start: 'top 82%',
            once: true,
          },
        });

        const horizontalTimeline = gsap.timeline({
          scrollTrigger: {
            trigger: shell,
            start: 'top top',
            end: () => `+=${distance()}`,
            pin: true,
            scrub: 0.9,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          },
        });

        horizontalTimeline
          .to(track, { x: () => -distance(), ease: 'none' }, 0)
          .to(progress, { scaleX: 1, ease: 'none' }, 0)
          .to(
            gsap.utils.toArray('.ding-journey__orbit', journey),
            { rotation: 22, xPercent: -12, ease: 'none', stagger: 0.04 },
            0
          )
          .to(
            gsap.utils.toArray('[data-ding-bean]', journey),
            {
              xPercent: (index) => (index % 2 ? -90 : 110),
              yPercent: (index) => (index % 3 ? -80 : 100),
              rotation: (index) => (index % 2 ? -140 : 180),
              ease: 'none',
              stagger: 0.015,
            },
            0
          );

        panels.forEach((panel) => {
          const visual = panel.querySelector('[data-ding-journey-media]');
          const image = visual?.querySelector('img');
          if (!visual) return;

          if (image) {
            gsap.to(image, {
              scale: 1.13,
              xPercent: -3,
              ease: 'none',
              scrollTrigger: {
                trigger: shell,
                start: 'top top',
                end: () => `+=${distance()}`,
                scrub: 1.15,
              },
            });
          }

          const rotateX = gsap.quickTo(visual, 'rotationX', { duration: 0.45, ease: 'power3.out' });
          const rotateY = gsap.quickTo(visual, 'rotationY', { duration: 0.45, ease: 'power3.out' });

          const onMove = (event) => {
            const bounds = visual.getBoundingClientRect();
            const x = (event.clientX - bounds.left) / bounds.width - 0.5;
            const y = (event.clientY - bounds.top) / bounds.height - 0.5;
            rotateX(y * -5);
            rotateY(x * 6);
          };
          const onLeave = () => {
            rotateX(0);
            rotateY(0);
          };

          visual.addEventListener('pointermove', onMove, { passive: true });
          visual.addEventListener('pointerleave', onLeave, { passive: true });
          cleanups.push(() => {
            visual.removeEventListener('pointermove', onMove);
            visual.removeEventListener('pointerleave', onLeave);
          });
        });

        return () => cleanups.forEach((cleanup) => cleanup());
      });

      media.add('(max-width: 989px) and (prefers-reduced-motion: no-preference)', () => {
        ScrollTrigger.batch('[data-ding-journey-panel]', {
          start: 'top 86%',
          once: true,
          onEnter: (panels) =>
            gsap.fromTo(
              panels,
              { autoAlpha: 0, y: 42 },
              { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.1, ease: 'power3.out' }
            ),
        });
      });

      media.add('(prefers-reduced-motion: reduce)', () => {
        stopLenis();
        gsap.set(
          '#MainContent .card-wrapper, #MainContent .multicolumn-card, #MainContent .collage-card, [data-ding-footer] .footer-block',
          { clearProps: 'all' }
        );
      });
    }, document.body);

    requestAnimationFrame(() => ScrollTrigger.refresh());
  }

  function refreshMotion() {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }, 160);
  }

  function boot() {
    initHeaderSubmenus();
    initMotion();
    window.addEventListener('load', refreshMotion, { once: true });
    window.addEventListener('resize', refreshMotion, { passive: true });

    document.addEventListener('shopify:section:load', (event) => {
      initHeaderSubmenus(event.target);
      initMotion();
    });
    document.addEventListener('shopify:section:reorder', initMotion);
    document.addEventListener('shopify:section:unload', initMotion);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.addEventListener(
    'pagehide',
    () => {
      if (motionContext) motionContext.revert();
      stopLenis();
    },
    { once: true }
  );
})();
